import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { cockpitInterestedBuyersWidgets } from '~/config/widgets/cockpit/cockpitInterestedBuyersWidgets';
import { AppLink } from '~/components/AppLink';
import { useAuthz } from '~/context/AuthzContext';
import {
  Accordion,
  Bars,
  ContactActions,
  EmptyBlock,
  fmtDate,
  Funnel,
  Ico,
  Kpi,
  PageHead,
  Progress,
  V3Page,
} from '~/components/cockpit/v3';
import type { WidgetProps } from '~/components/cockpit/cockpitData';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_INTEREST,
)({
  component: InterestPage,
});

interface Buyer {
  readonly id?: string;
  readonly firstname?: string;
  readonly lastname?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly mobile?: string;
  readonly city?: string;
  readonly message?: string;
  readonly status?: string;
  readonly hasDownloaded?: boolean;
  readonly hasOpened?: boolean;
  readonly hasBookedVisit?: boolean;
  readonly hasVisited?: boolean;
  readonly hasGivenOffer?: boolean;
  readonly reason?: string;
  readonly createdAt?: string;
}

const LOST_STATUS = [
  'notinterested',
  'no-feedback',
  'lost_interest',
];

/** Highest step reached: 1 downloaded ... 5 offer given. */
const progressOf = (b: Buyer): number =>
  b.hasGivenOffer
    ? 5
    : b.hasVisited
    ? 4
    : b.hasBookedVisit
    ? 3
    : b.hasOpened
    ? 2
    : 1;

const nameOf = (b: Buyer): string =>
  [
    b.firstname,
    b.lastname,
  ]
    .filter(Boolean)
    .join(' ') || '–';

// The loader always sends staff objects; an unresolved one has no name fields.
// HubDB records may carry only `name`.
function staffName(staff?: {
  firstname?: string;
  lastname?: string;
  name?: string;
}): string {
  return (
    [
      staff?.firstname,
      staff?.lastname,
    ]
      .filter(Boolean)
      .join(' ') ||
    staff?.name ||
    ''
  );
}

function InterestPage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const isAdvisor = atLeast('partner');

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_INTEREST,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = query.data?.widgetProps || {};
    const widgets = cockpitInterestedBuyersWidgets(widgetProps);
    return (
      <div className="h-full flex flex-col gap-y-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const wp = (query.data.widgetProps || {}) as WidgetProps;
  const list: Buyer[] = wp.cockpitInterestedBuyersDetailWidget?.data ?? [];
  const isLost = (b: Buyer): boolean => LOST_STATUS.includes(b.status ?? '');
  const active = list
    .filter((b) => !isLost(b))
    .sort((a, b) => progressOf(b) - progressOf(a));
  const lost = list.filter(isLost);
  const steps = [
    t('cockpit_interest_step_downloaded', 'Heruntergeladen'),
    t('cockpit_interest_step_opened', 'Geöffnet'),
    t('cockpit_interest_step_booked', 'Besichtigung gebucht'),
    t('cockpit_interest_step_visited', 'Besichtigt'),
    t('cockpit_interest_step_offer', 'Angebot abgegeben'),
  ];
  const lostLabels: Record<string, string> = {
    notinterested: t('cockpit_interest_lost_ni', 'Kein Interesse'),
    'no-feedback': t('cockpit_interest_lost_nf', 'Keine Rückmeldung'),
    lost_interest: t('cockpit_interest_lost_li', 'Interesse verloren'),
  };
  const agent = wp.cockpitInternalExternalAgentWidget ?? {};
  const internalName = staffName(agent.internalAgent);
  const externalName = staffName(agent.externalAgent);
  const staffCard =
    internalName || externalName ? (
      <section
        className="card"
        data-od-id="marketing-staff"
        data-widget="cockpitInternalExternalAgentWidget"
      >
        <div className="sec">
          <h2>{t('cockpit_interest_agents', 'Vermarktung')}</h2>
        </div>
        <dl className="dl">
          {internalName ? (
            <div>
              <dt>{t('cockpit_interest_agent_int', 'Intern')}</dt>
              <dd>{internalName}</dd>
            </div>
          ) : null}
          {externalName ? (
            <div>
              <dt>{t('cockpit_interest_agent_ext', 'Extern')}</dt>
              <dd>{externalName}</dd>
            </div>
          ) : null}
        </dl>
      </section>
    ) : null;
  const noReason = t('cockpit_interest_no_reason', 'Keine Angabe');
  const reached = (k: number): number =>
    list.filter((b) => progressOf(b) >= k).length;

  const head = (
    <PageHead
      odId="interest-head"
      title={t('cockpit_interest_title', 'Interessenten')}
      sub={t('cockpit_interest_sub', 'vom Exposé-Download bis zum Angebot')}
    />
  );

  if (list.length === 0) {
    return (
      <V3Page>
        {head}
        <EmptyBlock
          icon="users"
          title={t('cockpit_interest_empty_title', 'Noch keine Interessenten')}
          text={t(
            'cockpit_interest_empty_text',
            'Sobald jemand das Exposé herunterlädt, erscheint die Person hier – mit ihrem Fortschritt bis zur Besichtigung und zum Angebot.',
          )}
          actions={
            <AppLink
              to={AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME}
              params={params}
              className="btn"
              data-od-id="cta-back-dashboard"
            >
              {t('cockpit_interest_back', 'Zum ImmoCockpit-Dashboard')}
            </AppLink>
          }
        />
        {staffCard}
      </V3Page>
    );
  }

  const reasonCount = new Map<string, number>();
  lost.forEach((b) => {
    const r = b.reason || noReason;
    reasonCount.set(r, (reasonCount.get(r) ?? 0) + 1);
  });
  const reasons = [...reasonCount.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(
      ([
        label,
        value,
      ]) => ({ label, value }),
    );

  const who = (b: Buyer): React.ReactElement => (
    <span className="who">
      <b>{nameOf(b)}</b>
      {isAdvisor ? (
        <ContactActions
          name={nameOf(b)}
          phone={b.mobile || b.phone}
          email={b.email}
        />
      ) : null}
    </span>
  );
  const th = (key: string, def: string): React.ReactElement => (
    <th>{t(key, def)}</th>
  );

  return (
    <V3Page>
      {head}
      <div
        className="grid g4"
        data-od-id="kpi-row"
        data-widget="cockpitInterestedBuyers"
      >
        <Kpi
          odId="kpi-interested"
          value={active.length}
          delta={wp.cockpitInterestedBuyers?.changeInLastWeek}
          label={t('cockpit_interest_kpi_interested', 'Aktuell interessiert')}
        />
        <Kpi
          odId="kpi-visited"
          value={reached(4)}
          label={t('cockpit_interest_kpi_visited', 'Besichtigt')}
        />
        <Kpi
          odId="kpi-offers"
          value={reached(5)}
          label={t('cockpit_interest_kpi_offers', 'Angebot abgegeben')}
        />
        <Kpi
          odId="kpi-lost"
          value={lost.length}
          label={t('cockpit_interest_kpi_lost', 'Kein Interesse mehr')}
        />
      </div>

      <div className="grid split g2">
        <section
          className="card"
          data-od-id="funnel"
        >
          <div className="sec">
            <h2>
              {t('cockpit_interest_funnel', 'Fortschritt aller Interessenten')}
            </h2>
            <span className="xs mu">
              {list.length} {t('cockpit_interest_persons', 'Personen')}
            </span>
          </div>
          <div style={{ marginTop: 12 }}>
            <Funnel
              steps={steps.map((label, i) => ({
                label,
                value: reached(i + 1),
              }))}
              ariaLabel={t(
                'cockpit_interest_funnel_aria',
                'Trichter: Anzahl Interessenten je Fortschrittsstufe',
              )}
            />
          </div>
        </section>
        <section
          className="card"
          data-od-id="rejection-reasons"
          data-widget="cockpitRejectionReasonsGraphWidget"
        >
          <div className="sec">
            <h2>{t('cockpit_interest_reasons', 'Absagegründe')}</h2>
            <span className="xs mu">
              {lost.length} {t('cockpit_interest_rejections', 'Absagen')}
            </span>
          </div>
          <div style={{ marginTop: 12 }}>
            <Bars rows={reasons} />
          </div>
        </section>
      </div>

      <section
        className="card"
        data-od-id="buyers-active"
        data-widget="cockpitInterestedBuyersDetailWidget"
      >
        <div className="sec">
          <h2>{t('cockpit_interest_active', 'Aktive Interessenten')}</h2>
          <span className="xs mu">
            {t('cockpit_interest_sorted', 'sortiert nach Fortschritt')}
          </span>
        </div>
        {isAdvisor ? null : (
          <div
            className="callout"
            style={{ margin: '8px 0 12px' }}
          >
            <Ico
              name="info"
              size={18}
            />
            <span>
              {t(
                'cockpit_interest_privacy',
                'Sie sehen die Namen der Interessenten. E-Mail und Telefon sind Ihrem Berater vorbehalten.',
              )}
            </span>
          </div>
        )}
        <table className="tbl">
          <thead>
            <tr>
              {th('cockpit_interest_col_name', 'Name')}
              {th('cockpit_interest_col_city', 'Ort')}
              {th('cockpit_interest_col_status', 'Status')}
              {th('cockpit_interest_col_progress', 'Fortschritt')}
              {th('cockpit_interest_col_created', 'Erfasst')}
              {th('cockpit_interest_col_message', 'Nachricht')}
            </tr>
          </thead>
          <tbody>
            {active.map((b, i) => (
              <tr key={b.id ?? i}>
                <td data-l={t('cockpit_interest_col_name', 'Name')}>
                  {who(b)}
                </td>
                <td data-l={t('cockpit_interest_col_city', 'Ort')}>
                  {b.city || '–'}
                </td>
                <td data-l={t('cockpit_interest_col_status', 'Status')}>
                  <span className="chip c-info">
                    <i />
                    {t('cockpit_interest_interested', 'Interessiert')}
                  </span>
                </td>
                <td data-l={t('cockpit_interest_col_progress', 'Fortschritt')}>
                  <Progress
                    step={progressOf(b)}
                    labels={steps}
                  />
                </td>
                <td data-l={t('cockpit_interest_col_created', 'Erfasst')}>
                  {fmtDate(b.createdAt)}
                </td>
                <td
                  className="msg"
                  data-l={t('cockpit_interest_col_message', 'Nachricht')}
                >
                  {b.message || <span className="mu">–</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <Accordion
        odId="accordion-lost"
        label={t('cockpit_interest_lost_acc', 'Kein Interesse mehr')}
        count={lost.length}
      >
        <table className="tbl">
          <thead>
            <tr>
              {th('cockpit_interest_col_name', 'Name')}
              {th('cockpit_interest_col_city', 'Ort')}
              {th('cockpit_interest_col_status', 'Status')}
              {th('cockpit_interest_col_reason', 'Grund')}
              {th('cockpit_interest_col_progress', 'Fortschritt')}
              {th('cockpit_interest_col_created', 'Erfasst')}
            </tr>
          </thead>
          <tbody>
            {lost.map((b, i) => (
              <tr key={b.id ?? i}>
                <td data-l={t('cockpit_interest_col_name', 'Name')}>
                  {who(b)}
                </td>
                <td data-l={t('cockpit_interest_col_city', 'Ort')}>
                  {b.city || '–'}
                </td>
                <td data-l={t('cockpit_interest_col_status', 'Status')}>
                  <span className="chip c-neu">
                    <i />
                    {lostLabels[b.status ?? ''] ?? b.status}
                  </span>
                </td>
                <td data-l={t('cockpit_interest_col_reason', 'Grund')}>
                  {b.reason || noReason}
                </td>
                <td data-l={t('cockpit_interest_col_progress', 'Fortschritt')}>
                  <Progress
                    step={progressOf(b)}
                    labels={steps}
                  />
                </td>
                <td data-l={t('cockpit_interest_col_created', 'Erfasst')}>
                  {fmtDate(b.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Accordion>
      {staffCard}
    </V3Page>
  );
}
